import "@/App.css";
import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/context/LanguageContext";
import { Navbar } from "@/components/landing/Navbar";
import { Hero } from "@/components/landing/Hero";
import { BrandCarousel } from "@/components/landing/BrandCarousel";
import { About } from "@/components/landing/About";
import { Services } from "@/components/landing/Services";
import { Vision } from "@/components/landing/Vision";
import { Portfolio } from "@/components/landing/Portfolio";
import { Contact } from "@/components/landing/Contact";
import { Footer } from "@/components/landing/Footer";
import ProjectDetail from "@/components/landing/ProjectDetail";
import { Seo } from "@/components/Seo";

const LandingPage = () => (
  <>
    <main>
      <Hero />
      <BrandCarousel />
      <About />
      <Services />
      <Portfolio />
      <Vision />
      <Contact />
    </main>
    <Footer />
  </>
);

const ProjectPage = () => (
  <>
    <ProjectDetail />
    <Footer />
  </>
);

const RouteEffects = () => {
  const { hash, pathname } = useLocation();
  const previousPathname = useRef(pathname);

  useEffect(() => {
    const isRouteChange = previousPathname.current !== pathname;
    previousPathname.current = pathname;

    if (hash) {
      let attempts = 0;
      let retryId;
      const scrollToTarget = () => {
        const target = document.querySelector(hash);
        if (target) {
          if (isRouteChange) {
            const previousBehavior = document.documentElement.style.scrollBehavior;
            document.documentElement.style.scrollBehavior = "auto";
            target.scrollIntoView();
            window.requestAnimationFrame(() => {
              document.documentElement.style.scrollBehavior = previousBehavior;
            });
            return;
          }
          target.scrollIntoView({ behavior: "smooth" });
          return;
        }
        if (attempts < 16) {
          attempts += 1;
          retryId = window.setTimeout(scrollToTarget, 50);
        }
      };

      window.requestAnimationFrame(scrollToTarget);
      return () => window.clearTimeout(retryId);
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [hash, pathname]);

  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <>
      <RouteEffects />
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 8, scale: 0.996, filter: "blur(3px)" }}
          animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -5, scale: 0.998, filter: "blur(2px)" }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="min-h-screen will-change-transform"
        >
          <Routes location={location}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/project/:projectId" element={<ProjectPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

function App() {
  return (
    <LanguageProvider>
      <div className="App min-h-screen bg-[#050507] text-white antialiased selection:bg-[#00F0FF] selection:text-black">
        <Seo />
        <Navbar />
        <AnimatedRoutes />
        <Toaster theme="dark" position="bottom-right" />
      </div>
    </LanguageProvider>
  );
}

export default App;
